import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {SpatialOutageEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {OutageRendererService} from '@core/utils/engin/geospatial-viewer/renderers/outage-renderer.service';
import {OutageFieldsService} from '@core/utils/engin/geospatial-viewer/fields/outage-fields.service';
import {VisualizationColorCode, VisualizationType} from '../../../../../../pages/geospatial-viewer/model/visualization';

@Injectable()
export class OutagePointLayerService {
    constructor(
        private genericPopupService: GenericPopupService,
        private rendererService: OutageRendererService,
        private fieldsService: OutageFieldsService,
    ) {}

    /**
     * Prepare point layer (geometry type = point).
     * - No symbol; render a label above the point.
     * @return FeatureLayer
     */
    public preparePointLayer(
        pointData: SpatialOutageEntityDataExtended[],
        renderedVisualizationType: VisualizationType,
        id: string,
        theme: string,
        metrics: any[] = [],
    ): FeatureLayer {
        // Zoomed in: render point asset layer (+ outline as separate layer), and linear asset layer distinctly
        const pointSource = this.createPointGraphics(pointData);

        const pointPopupTemplate = {
            title: '{id}',
            outFields: ['*'],
            content: this.genericPopupService.getGenericOutageTooltipFunction.bind(
                this.genericPopupService,
                metrics,
                renderedVisualizationType,
            ),
        };

        const label = {
            labelExpressionInfo: {
                expression: '$feature.label',
            },
            symbol: {
                type: 'text',
                color: 'white',
                backgroundColor: 'transparent',
                yoffset: 0,
                font: {
                    weight: 'bold',
                    size: '12px',
                },
                haloColor: 'black',
                haloSize: 1,
            },
            labelPlacement: 'center-center',
        };
        return new FeatureLayer({
            title: 'outage_point_layer',
            id: id ? id : 'outage_point_layer',
            source: pointSource,
            renderer: this.rendererService.outagePointRenderer(theme),
            fields: this.fieldsService.getMapFields(),
            outFields: ['*'],
            // @ts-ignore
            labelingInfo: [
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '12px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR1'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '14px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR2'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '18px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR3'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '20px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR4'`,
                },
                {
                    ...label,
                    symbol: {
                        ...label.symbol,
                        // @ts-ignore
                        font: {
                            ...label.symbol.font,
                            size: '24px',
                        },
                    },
                    where: `colour = 'OUTAGE_COLOUR5'`,
                },
            ],
            popupTemplate: pointPopupTemplate,
        });
    }

    /*
     * Create array of Graphics, type = point. Return Graphic[]
     */
    private createPointGraphics(pointData: SpatialOutageEntityDataExtended[]): Graphic[] {
        return pointData.map((outage: SpatialOutageEntityDataExtended) => {
            const point = new Point({
                latitude: outage.geoJsonGeometry.coordinates[1],
                longitude: outage.geoJsonGeometry.coordinates[0],
            });

            return new Graphic({
                geometry: point,
                attributes: {...outage},
            });
        });
    }
}
